/**
 * Salesforce Data 360 CDP Utility Library
 * Shared utilities for personalization across all Shopify sections
 */

/* Guarded: this file is <script src>'d once per personalized section instance,
   so a page with several personalized sections loads it more than once. */
(function () {
  if (window.D360Utils) return;

  // Cookie reader
  const getCookie = (name) => {
    const value = `; ${document.cookie}`;
    const parts = value.split(`; ${name}=`);
    if (parts.length === 2) return parts.pop().split(';').shift();
  };

  // Environment-aware cookie + endpoint resolution using metaobject data
  const getD360Config = (metaobjectData) => ({
    cookie: metaobjectData.cookie,
    endpoint: metaobjectData.url,
  });

  // Get Salesforce environment configuration from injected data (set by Liquid template)
  const getSalesforceEnvironmentConfig = (environmentName) => {
    try {
      // Check if global config is available (set by Liquid template)
      if (!window.D360EnvironmentConfigs) {
        return null;
      }

      const config = window.D360EnvironmentConfigs[environmentName];

      if (!config) {
        return null;
      }

      return config;
    } catch {
      return null;
    }
  };

  // Decisioning API call with AbortController timeout
  const callDecisioningAPI = async (endpoint, body, timeoutMs) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), timeoutMs);
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      clearTimeout(timer);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null; // timeout or network error — silent in prod
    }
  };

  // Shopify Analytics event publisher
  const publishPersonalizationEvent = (eventName, payload) => {
    if (typeof Shopify !== 'undefined' && Shopify.analytics) {
      Shopify.analytics.publish(eventName, payload);
    }
  };

  // Mock data processor for testing
  const processMockData = (mockResponse, personalizationPointName) => {
    try {
      const mockData = JSON.parse(mockResponse);
      const personalization = mockData.personalizations.find(
        (p) => p.personalizationPointName === personalizationPointName,
      );

      if (personalization && personalization.attributes && personalization.attributes.Experience_ID) {
        return personalization.attributes.Experience_ID;
      }
      return null;
    } catch {
      return null;
    }
  };

  // Publish personalization view event
  const publishPersonalizationViewEvent = (personalizationData) => {
    const payload = {
      personalizationId: personalizationData.personalizationId,
      personalizationPointId: personalizationData.personalizationPointId,
      personalizationDecisionId: personalizationData.personalizationDecisionId,
      interactionName: 'personalization-view',
    };

    publishPersonalizationEvent('personalization-view', payload);
  };

  const publishPersonalizationClickEvent = (personalizationData, webInteraction) => {
    const payload = {
      personalizationId: personalizationData.personalizationId,
      personalizationPointId: personalizationData.personalizationPointId,
      personalizationDecisionId: personalizationData.personalizationDecisionId,
      interactionName: 'personalization-click',
      webInteraction: {
        name: (webInteraction && webInteraction.name) || '',
        URL: (webInteraction && webInteraction.url) || '',
        region: (webInteraction && webInteraction.region) || '',
      },
    };

    publishPersonalizationEvent('personalization-click', payload);
  };

  // Fallback (non-personalized) render events. Fired only when a section
  // actually renders its fallback experience, never when the section is hidden.
  const buildFallbackPayload = (eventName, pointName) => ({
    personalizationId: pointName || '',
    personalizationPointId: pointName || '',
    personalizationDecisionId: null,
    interactionName: eventName,
  });

  const publishFallbackViewEvent = (pointName) => {
    publishPersonalizationEvent('personalization-view', buildFallbackPayload('personalization-view', pointName));
  };

  const publishFallbackClickEvent = (pointName, webInteraction) => {
    publishPersonalizationEvent('personalization-click', {
      ...buildFallbackPayload('personalization-click', pointName),
      webInteraction: {
        name: (webInteraction && webInteraction.name) || '',
        URL: (webInteraction && webInteraction.url) || '',
        region: (webInteraction && webInteraction.region) || '',
      },
    });
  };

  // Main personalization orchestrator
  const getPersonalizedExperience = async (config) => {
    const {
      personalizationPointName,
      fallbackExperienceId,
      maxWaitTime = 2000,
      environmentName = window.D360ThemeSettings?.selectedEnvironment,
      mockResponse = null,
      enableDebugLogging = false,
    } = config;

    const log = enableDebugLogging ? console.log : () => {};

    log('D360: Starting personalization for', personalizationPointName);

    // Mock data is a testing shortcut and must not depend on a live
    // environment config or a real visitor cookie being present — both of
    // which are commonly missing in the Shopify theme editor's admin
    // iframe. Check it first so "Mock Response (Testing Only)" actually
    // works there.
    if (mockResponse) {
      const mockExperienceId = processMockData(mockResponse, personalizationPointName);
      if (mockExperienceId) {
        log('D360: Using mock experience', mockExperienceId);
        return {
          experienceId: mockExperienceId,
          isPersonalized: true,
          personalizationData: {
            personalizationId: generateRandomEventId(),
            personalizationPointId: personalizationPointName,
            personalizationDecisionId: 'mock-decision-id',
          },
        };
      }
      log('D360: Mock data had no matching experience for this point, continuing to live lookup');
    }

    // Get environment configuration from injected data (set by Liquid template)
    const environmentConfig = getSalesforceEnvironmentConfig(environmentName);

    if (!environmentConfig) {
      log('D360: Failed to fetch environment configuration, using fallback');
      return { experienceId: fallbackExperienceId, isPersonalized: false };
    }

    // Get visitor ID from cookie
    const d360Config = getD360Config(environmentConfig);
    let visitorId = getCookie(d360Config.cookie);

    // Fallback: try alternative cookie names if primary not found
    if (!visitorId) {
      const alternativeCookies = ['_d360sdk_dev', '_d360sdk_prod'];
      for (const altCookie of alternativeCookies) {
        visitorId = getCookie(altCookie);
        if (visitorId) break;
      }
    }

    if (!visitorId) {
      return null; // Return null to indicate no personalization should be applied
    }

    // Make live API call
    log('D360: Making live API call to', d360Config.endpoint);
    const requestBody = {
      context: {
        individualId: visitorId,
        requestUrl: window.location.href,
      },
      personalizationPoints: [
        {
          name: personalizationPointName,
        },
      ],
    };

    log('D360: Request body:', JSON.stringify(requestBody, null, 2));

    try {
      const response = await callDecisioningAPI(d360Config.endpoint, requestBody, maxWaitTime);

      if (!response) {
        log('D360: API call failed or timed out, using fallback');
        return { experienceId: fallbackExperienceId, isPersonalized: false };
      }

      log('D360: API response received', response);

      // Extract Experience_ID from response
      if (response.personalizations && response.personalizations.length > 0) {
        const firstPersonalization = response.personalizations[0];

        if (firstPersonalization.attributes && firstPersonalization.attributes.Experience_ID) {
          const experienceId = firstPersonalization.attributes.Experience_ID;
          log('D360: API returned experience', experienceId);

          return {
            experienceId: experienceId,
            isPersonalized: true,
            personalizationData: {
              personalizationId: firstPersonalization.personalizationId,
              personalizationPointId: firstPersonalization.personalizationPointId || personalizationPointName,
              personalizationDecisionId: firstPersonalization.decisionId || 'unknown-decision-id',
            },
            fullResponse: firstPersonalization,
          };
        }
      }

      // Handle empty personalizations array or missing Experience_ID
      log('D360: API response had no Experience_ID, using fallback');
      return { experienceId: fallbackExperienceId, isPersonalized: false };
    } catch (error) {
      log('D360: API call threw error, using fallback', error);
      return { experienceId: fallbackExperienceId, isPersonalized: false };
    }
  };

  // Generate random event ID for personalization tracking
  const generateRandomEventId = () => {
    return 'evt_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  };

  // Export utilities for use in other scripts
  window.D360Utils = {
    getCookie,
    getD360Config,
    getSalesforceEnvironmentConfig,
    callDecisioningAPI,
    publishPersonalizationEvent,
    publishPersonalizationViewEvent,
    publishPersonalizationClickEvent,
    publishFallbackViewEvent,
    publishFallbackClickEvent,
    processMockData,
    getPersonalizedExperience,
    generateRandomEventId,
  };
})();
